<template>
  <div class="wiki-detail-page">
    <AppHeader />

    <div v-if="loading" class="loading-container">
      <el-icon class="is-loading" :size="40"><Loading /></el-icon>
      <p>加载文档中...</p>
    </div>

    <template v-else-if="wikiData">
      <div class="detail-body-wrapper">
        <div class="detail-inner">
          <!-- 左侧：文档目录面板 -->
          <aside class="doc-sidebar">
            <!-- 文档信息卡片 -->
            <div class="doc-info-card">
              <div class="doc-info-header">
                <div class="doc-icon-wrapper">
                  <el-icon :size="22"><Document /></el-icon>
                </div>
                <div class="doc-info-text">
                  <h2 class="doc-title">{{ wikiData.name }}</h2>
                  <p class="doc-subtitle">{{ wikiData.model || wikiData.docType || '技术文档' }}</p>
                </div>
              </div>
              <div class="doc-badges">
                <span class="badge badge-primary">{{ categoryName }}</span>
                <span class="badge badge-green" v-if="wikiData.docType">{{ wikiData.docType }}</span>
                <span class="badge badge-purple" v-if="wikiData.tag">{{ wikiData.tag }}</span>
              </div>
              <div class="doc-meta">
                <span><el-icon><Calendar /></el-icon> {{ formatDate(wikiData.updateTime || wikiData.createTime) }}</span>
                <span><el-icon><View /></el-icon> {{ wikiData.views || 0 }} 次浏览</span>
              </div>
              <p class="doc-desc" v-if="wikiData.description">{{ wikiData.description }}</p>
            </div>

            <!-- PDF 文件目录 -->
            <div class="doc-directory">
              <div class="directory-header">
                <el-icon :size="16"><FolderOpened /></el-icon>
                <span>文件目录</span>
                <span class="directory-count">{{ pdfFiles.length }} 个文件</span>
              </div>
              <div class="directory-list" v-if="pdfFiles.length > 0">
                <div
                  v-for="(pdf, index) in pdfFiles"
                  :key="pdf.id"
                  class="directory-item"
                  :class="{ active: pdf.id === currentPdfId }"
                  @click="handlePdfSwitch(pdf.id)"
                >
                  <div class="item-icon" :class="{ active: pdf.id === currentPdfId }">
                    <el-icon :size="16"><Document /></el-icon>
                  </div>
                  <div class="item-info">
                    <span class="item-name">{{ pdf.originalName || getFileName(pdf.filePath) }}</span>
                    <span class="item-size" v-if="pdf.fileSize">{{ formatFileSize(pdf.fileSize) }}</span>
                  </div>
                  <div class="item-actions">
                    <el-icon
                      class="download-icon"
                      :size="14"
                      @click.stop="downloadPdf(pdf.id)"
                      title="下载"
                    ><Download /></el-icon>
                  </div>
                  <div class="active-indicator" v-if="pdf.id === currentPdfId"></div>
                </div>
              </div>
              <div class="directory-empty" v-else>
                <el-icon :size="32"><FolderOpened /></el-icon>
                <p>暂无 PDF 文件</p>
              </div>
            </div>

            <!-- 操作按钮 -->
            <div class="doc-actions">
              <el-button type="primary" :icon="Download" @click="downloadPdf(currentPdfId)" :disabled="!currentPdfId" round>
                下载当前文档
              </el-button>
              <el-button :icon="ArrowLeft" @click="goBack" round>返回列表</el-button>
            </div>
          </aside>

          <!-- 右侧：PDF 预览区 -->
          <main class="pdf-viewer">
            <div class="viewer-toolbar" v-if="pdfFiles.length > 0">
              <div class="viewer-title">
                <el-icon :size="16"><Document /></el-icon>
                <span>{{ currentPdfName }}</span>
              </div>
              <div class="viewer-actions">
                <el-button size="small" text :icon="Download" @click="downloadPdf(currentPdfId)">下载</el-button>
              </div>
            </div>
            <div class="viewer-frame">
              <iframe v-if="previewUrl" :src="previewUrl" class="pdf-iframe"></iframe>
              <div v-else-if="pdfFiles.length === 0" class="viewer-empty">
                <el-icon :size="56"><Document /></el-icon>
                <p>该文档暂无 PDF 文件</p>
                <span>请从左侧目录选择文件，或联系管理员上传</span>
              </div>
              <div v-else class="viewer-empty">
                <el-icon :size="40" class="is-loading"><Loading /></el-icon>
                <p>正在加载预览...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    </template>

    <div v-else class="not-found">
      <el-icon :size="64"><Document /></el-icon>
      <h2>文档不存在</h2>
      <p>请检查链接是否正确，或返回 Wiki 列表</p>
      <el-button type="primary" @click="goBack">返回 Wiki 列表</el-button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Document, Loading, Calendar, View, Download, ArrowLeft, FolderOpened } from '@element-plus/icons-vue'
import AppHeader from '@/components/AppHeader.vue'
import { getWikiDetail, getWikiPreview, downloadWikiPdf, incrementWikiViews } from '@/api/wiki'
import { getCategories } from '@/api/product'
import { formatDate } from '@/utils/dateFormat'

const router = useRouter()
const route = useRoute()

const loading = ref(false)
const wikiData = ref(null)
const previewUrl = ref('')
const allCategories = ref([])
const currentPdfId = ref(null)

const pdfFiles = computed(() => wikiData.value?.pdfFiles || [])

const currentPdfName = computed(() => {
  const pdf = pdfFiles.value.find(p => p.id === currentPdfId.value)
  return pdf?.originalName || getFileName(pdf?.filePath) || '文档预览'
})

const categoryName = computed(() => {
  const categoryId = wikiData.value?.categoryId || wikiData.value?.category?.id
  const subcategoryId = wikiData.value?.subcategoryId || wikiData.value?.subcategory?.id
  const category = allCategories.value.find(item => Number(item.id) === Number(categoryId))
  const subcategory = allCategories.value.find(item => Number(item.id) === Number(subcategoryId))
  if (subcategory?.name) return `${category?.name || ''} / ${subcategory.name}`.trim()
  return category?.name || '未分类'
})

function getFileName(filePath) {
  if (!filePath) return ''
  return filePath.split('/').pop()
}

function formatFileSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const loadCategories = async () => {
  try {
    const res = await getCategories()
    allCategories.value = Array.isArray(res.data) ? res.data : (res.data?.list || [])
  } catch (error) {
    console.error('加载分类失败:', error)
  }
}

const loadWikiDetail = async (id) => {
  loading.value = true
  try {
    const res = await getWikiDetail(id)
    const detail = res.data || res
    wikiData.value = detail

    if (detail?.id) {
      incrementWikiViews(detail.id).catch(() => {})
    }

    const pdfs = detail?.pdfFiles || []
    currentPdfId.value = pdfs.length ? pdfs[0].id : null
    await loadPreview(currentPdfId.value)
  } catch (error) {
    console.error('加载 Wiki 详情失败:', error)
    wikiData.value = null
    ElMessage.error('加载文档失败')
  } finally {
    loading.value = false
  }
}

const loadPreview = async (fileId) => {
  previewUrl.value = ''
  if (!fileId) return
  try {
    const previewRes = await getWikiPreview(route.params.id, fileId)
    const blob = previewRes.data || previewRes
    if (blob instanceof Blob) {
      previewUrl.value = URL.createObjectURL(blob)
    } else if (typeof blob === 'string') {
      previewUrl.value = blob
    }
  } catch (error) {
    console.error('加载预览失败:', error)
  }
}

const handlePdfSwitch = (fileId) => {
  if (currentPdfId.value === fileId) return
  currentPdfId.value = fileId
  loadPreview(fileId)
}

const downloadPdf = async (fileId) => {
  try {
    const res = await downloadWikiPdf(route.params.id, fileId)
    const blob = res.data || res
    if (blob instanceof Blob) {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const pdf = pdfFiles.value.find(p => p.id === fileId)
      link.download = pdf?.originalName || `${wikiData.value?.name || 'wiki'}.pdf`
      link.click()
      window.URL.revokeObjectURL(url)
    } else {
      ElMessage.warning('当前文档暂无可下载文件')
    }
  } catch (error) {
    console.error('下载 PDF 失败:', error)
    ElMessage.error('下载文档失败')
  }
}

const goBack = () => router.push('/wikis')

watch(
  () => route.params.id,
  (newId) => {
    if (newId) loadWikiDetail(newId)
  },
  { immediate: true }
)

onMounted(() => {
  loadCategories()
})
</script>

<style scoped>
.wiki-detail-page {
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  padding-top: 72px;
  box-sizing: border-box;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #6b7280;
  gap: 16px;
}

.detail-body-wrapper {
  flex: 1;
  min-height: 0;
  padding: 20px 24px;
}

.detail-inner {
  display: flex;
  gap: 20px;
  height: 100%;
}

/* ===== 左侧文档目录面板 ===== */
.doc-sidebar {
  width: 320px;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  scrollbar-width: thin;
}

.doc-info-card {
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  border: 1px solid #e8ecf0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}

.doc-info-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.doc-icon-wrapper {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.doc-info-text {
  flex: 1;
  min-width: 0;
}

.doc-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 3px 0;
  line-height: 1.3;
  word-break: break-all;
}

.doc-subtitle {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
}

.doc-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.badge {
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 100px;
}

.badge-primary {
  color: #4f46e5;
  background: rgba(79, 70, 229, 0.08);
}

.badge-green {
  color: #059669;
  background: rgba(5, 150, 105, 0.08);
}

.badge-purple {
  color: #7c3aed;
  background: rgba(124, 58, 237, 0.08);
}

.doc-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 10px;
}

.doc-meta span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.doc-desc {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ===== 文件目录 ===== */
.doc-directory {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e8ecf0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  overflow: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.directory-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
  background: #fafbfc;
}

.directory-count {
  margin-left: auto;
  font-size: 11px;
  font-weight: 400;
  color: #9ca3af;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 10px;
}

.directory-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  scrollbar-width: thin;
}

.directory-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  margin-bottom: 4px;
}

.directory-item:hover {
  background: #f8fafc;
}

.directory-item.active {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
}

.directory-item:not(.active) {
  border: 1px solid transparent;
}

.item-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.item-icon.active {
  background: #dbeafe;
  color: #2563eb;
}

.item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.item-name {
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.directory-item.active .item-name {
  color: #1d4ed8;
}

.item-size {
  font-size: 11px;
  color: #9ca3af;
}

.item-actions {
  opacity: 0;
  transition: opacity 0.2s;
}

.directory-item:hover .item-actions {
  opacity: 1;
}

.download-icon {
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.15s;
}

.download-icon:hover {
  color: #2563eb;
  background: #eff6ff;
}

.active-indicator {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 20px;
  border-radius: 0 3px 3px 0;
  background: #2563eb;
}

.directory-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #d1d5db;
  gap: 10px;
}

.directory-empty p {
  margin: 0;
  font-size: 13px;
  color: #9ca3af;
}

/* ===== 操作按钮 ===== */
.doc-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.doc-actions .el-button {
  flex: 1;
}

/* ===== 右侧 PDF 预览区 ===== */
.pdf-viewer {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e8ecf0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  overflow: hidden;
}

.viewer-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 18px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafbfc;
  flex-shrink: 0;
}

.viewer-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  min-width: 0;
}

.viewer-title span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.viewer-frame {
  flex: 1;
  min-height: 0;
  position: relative;
  background: #525659;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}

.viewer-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  gap: 12px;
  background: #f9fafb;
}

.viewer-empty p {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: #6b7280;
}

.viewer-empty span {
  font-size: 13px;
  color: #9ca3af;
}

/* ===== 404 ===== */
.not-found {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #9ca3af;
  gap: 12px;
}

.not-found h2 {
  color: #374151;
  margin: 0;
}

/* ===== 响应式 ===== */
@media (max-width: 1024px) {
  .doc-sidebar {
    width: 280px;
    min-width: 280px;
  }
}

@media (max-width: 768px) {
  .detail-body-wrapper {
    padding: 12px;
  }
  .detail-inner {
    flex-direction: column;
  }
  .doc-sidebar {
    width: 100%;
    min-width: 100%;
    max-height: 45%;
    overflow-y: auto;
  }
  .pdf-viewer {
    min-height: 400px;
  }
  .doc-actions {
    flex-wrap: wrap;
  }
}
</style>
